import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription } from 'rxjs';

import { CurrencyPipe } from '../../pipes/currency.pipe';
import { PaymentMethodDiscount, TableService } from '../../domain/services/table.service';
import { Table } from '../../domain/models/table';
import { UserService } from '../../domain/services/user.service';
import { PaymentValuesService } from '../../domain/services/payment-values.service';

@Component({
  selector: 'app-table-values-summary-list',
  standalone: true,
  imports: [CommonModule, CurrencyPipe],
  templateUrl: './table-values-summary-list.component.html',
  styleUrls: ['./table-values-summary-list.component.scss']
})

export class TableValuesSummaryListComponent implements OnInit, OnDestroy {

  @Input() hasCashback: boolean = false;

  @Input() customPayment: string;

  /** override do "Restante a pagar". Se não passado, lê de `table.remaining` (back). */
  @Input() remainingValue?: number | null;
  /** override do "Total". Pai passa quando quer mostrar valor diferente do `table.tableFinalValue`. */
  @Input() total?: number | null;
  /** override do "Subtotal". Se não passado, lê de `table.subtotal` (back). */
  @Input() subtotal?: number | null;
  /** override da "Taxa de serviço". Se não passado, lê de `table.serviceFeeValue` (back). */
  @Input() serviceFeeValue?: number | null;
  /** override do "Valor final da mesa". Se não passado, lê de `table.tableFinalValue` (back). */
  @Input() tableFinalValue?: number | null;
  /** desconto por forma de pagamento (vindo do TableService em runtime). */
  @Input() paymentMethodDiscount: PaymentMethodDiscount | null = null;
  /** controla se o desconto por forma de pagamento deve ser exibido. */
  @Input() showPaymentMethodDiscount: boolean = false;
  /** valor do cashback disponível para resgate. */
  @Input() cashbackValue: number = 0;

  table: Table;
  isLoggedIn: boolean = false;

  private tableSubscription: Subscription;
  private paymentMethodDiscountSubscription: Subscription;
  private authStateSubscription: Subscription;

  constructor(
    private tableService: TableService,
    private userService: UserService,
    private paymentValuesService: PaymentValuesService,
  ) {}

  ngOnInit(): void {
    this.table = this.tableService.getTable();
    this.isLoggedIn = this.userService.checkUser() === 'AUTHORIZED';

    this.tableSubscription = this.tableService.$openedTable.subscribe((table) => {
      if (table) {
        this.table = table;
      }
    });

    this.paymentMethodDiscountSubscription = this.tableService.$paymentMethodDiscount.subscribe((discount) => {
      this.paymentMethodDiscount = discount;
    });

    this.authStateSubscription = this.userService.$authState.subscribe((authState) => {
      this.isLoggedIn = authState === 'AUTHORIZED';
    });
  }

  ngOnDestroy(): void {
    this.tableSubscription?.unsubscribe();
    this.paymentMethodDiscountSubscription?.unsubscribe();
    this.authStateSubscription?.unsubscribe();
  }

  private get isCustom(): boolean {
    return this.table?.paymentType === 'CUSTOM';
  }

  /** Arredondamento monetário pra 2 casas — evita 3+ decimais no template. */
  private round2(n: number | null | undefined): number {
    return Math.round((Number(n) || 0) * 100) / 100;
  }

  /** valor (numérico) que o usuário digitou em "Pagar valor personalizado". */
  get customPaymentValue(): number {
    return this.round2(Number(this.table?.customPayment) || 0);
  }

  /** valor exibido no "Subtotal" — input do pai ou campo canônico do back.
   *  CUSTOM: cheio da mesa (resumo é vista geral; "Vai pagar" mostra o parcial). */
  get displaySubtotal(): number {
    if (this.subtotal != null) return this.round2(this.subtotal);
    return this.round2(this.table.subtotal);
  }

  /** valor exibido no "Taxa de serviço" — proporcional em PARTIAL, cheio em TOTAL/CUSTOM. */
  get displayServiceFeeValue(): number {
    if (this.serviceFeeValue != null) return this.round2(this.serviceFeeValue);
    if (this.isCustom) return this.round2(this.table.serviceFeeValue);
    return this.round2(this.paymentValuesService.getProportionalServiceFee(this.table));
  }

  /** valor exibido no "Total" — input do pai ou campo canônico do back. */
  get displayTableFinalValue(): number {
    return this.round2(this.tableFinalValue ?? this.table.tableFinalValue);
  }

  /** valor exibido na linha "Total" do template — pai pode sobrescrever via `total` (ex: pagamento parcial mostra valor da seleção).
   *  CUSTOM: abate fidelity do tableFinalValue (back não desconta — só voucher/cashback).
   *  TOTAL sem pagamento prévio: abate o desconto da forma de pagamento (PIX, etc).
   *  `paymentMethodDiscountValue` já é gateado por paymentType/paid, retorna 0 fora desse cenário. */
  get displayTotal(): number {
    if (this.total != null) {
      return this.round2(Math.max(0, this.round2(this.total) - this.paymentMethodDiscountValue));
    }
    if (this.isCustom) {
      return this.round2(Math.max(0, this.displayTableFinalValue - this.displayFidelityDiscount));
    }
    return this.round2(Math.max(0, this.displayTableFinalValue - this.paymentMethodDiscountValue));
  }

  /** valor exibido no "Restante" — input do pai ou campo canônico do back. */
  get displayRemainingValue(): number {
    return this.round2(this.remainingValue ?? this.table.remaining);
  }

  /**
   * Percentual da taxa de serviço (apresentação visual). Lê direto do back via
   * `tipValue` (product_tip.tax_type no node-api, propagado pelo nest no
   * qrcode response). Mostra a % exata configurada no admin.
   */
  get serviceFeePercent(): number {
    if (this.displayServiceFeeValue <= 0 || this.table.tipType !== 'percent') {
      return 0;
    }
    return Math.round(Number(this.table?.tipValue) || 0);
  }

  get paymentMethodDiscountValue(): number {
    if (!this.paymentMethodDiscount || this.paymentMethodDiscount.value <= 0) {
      return 0;
    }
    // Desconto por forma de pagamento só existe ao pagar o valor TOTAL da mesa,
    // sem nenhum pagamento prévio. Em PARTIAL/CUSTOM ou TOTAL após pagamento
    // parcial não aplica — esconde a linha do resumo e não abate do total.
    if (this.table?.paymentType !== 'TOTAL') return 0;
    if ((Number(this.table?.paid) || 0) > 0) return 0;
    if (this.paymentMethodDiscount.type === 'PERCENTAGE') {
      // Último da fila — depois de manual+acréscimo+cupom+fidelity+cashback.
      // Base = `remaining` (o valor final pré-pix).
      const base = Number(this.table?.remaining) || 0;
      return this.round2((this.paymentMethodDiscount.value / 100) * base);
    }
    return this.round2(this.paymentMethodDiscount.value);
  }

  /** Acréscimo (auto-incremento %) que pesa neste pagamento — proporcional em PARTIAL, cheio em TOTAL/CUSTOM. */
  get displayTax(): number {
    if (this.isCustom) return this.round2(this.table.tax);
    return this.round2(this.paymentValuesService.getProportionalTax(this.table));
  }

  /** Cupom % que pesa neste pagamento — proporcional em PARTIAL, cheio em TOTAL/CUSTOM. */
  get displayVoucherDiscount(): number {
    if (this.isCustom) {
      const v = this.table.vouchers?.[0];
      return v ? this.round2(v.discountValueApplied) : 0;
    }
    return this.round2(this.paymentValuesService.getProportionalVoucherDiscount(this.table));
  }

  /** Fidelidade que pesa neste pagamento — proporcional em PARTIAL, cheio em TOTAL/CUSTOM. */
  get displayFidelityDiscount(): number {
    if (this.isCustom) {
      const f = this.table.fidelity;
      if (!f) return 0;
      if (f.discountType === 'VALUE') return this.round2(f.discountValue);
      const base = Number(this.table.tableFinalValue) || 0;
      return this.round2((base * (Number(f.discountValue) || 0)) / 100);
    }
    return this.round2(this.paymentValuesService.getProportionalFidelityDiscount(this.table));
  }

  /** Desconto "manual" da mesa, sem o cashback — o cashback tem linha própria e
   *  agora vive dentro de table.discount (foldado no back). No GD discount ==
   *  cashback, então zera e a linha "Desconto" some (evita mostrar duplicado). */
  get displayManualDiscount(): number {
    const discount = this.round2(this.table?.discount || 0);
    const cashback = this.round2(this.cashbackValue || 0);
    return this.round2(Math.max(0, discount - cashback));
  }
}